import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Mail, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import { CONTACT } from "@/assets";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full bg-transparent border-0 border-b border-white/20 focus:border-[#00E5FF] focus:ring-0 outline-none px-0 py-4 text-lg md:text-xl text-[#F4F4F0] placeholder:text-[#8A949E]/60 transition-colors duration-300";

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [status, setStatus] = useState("idle");

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        try {
            await axios.post(`${API}/contact`, {
                name: form.name,
                email: form.email || null,
                phone: form.phone || null,
                message: form.message,
            });
            setStatus("success");
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch {
            setStatus("error");
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative py-28 md:py-40 border-t border-white/10">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
                    <div>
                        <Reveal>
                            <p className="text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4">Get in touch</p>
                            <h2 className="font-display tracking-tighter text-[15vw] lg:text-[7rem] leading-[0.9] text-[#F4F4F0]">
                                LET'S<br />
                                <span className="italic text-[#00E5FF]">TALK</span>
                            </h2>
                        </Reveal>

                        <Reveal delay={0.15} className="mt-14 flex flex-col gap-8">
                            <a
                                href={CONTACT.phoneHref}
                                data-testid="contact-phone-link"
                                className="group flex items-center gap-5 w-fit"
                            >
                                <span className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-[#00E5FF] group-hover:border-[#00E5FF] transition-colors duration-300">
                                    <Phone className="w-5 h-5 text-[#F4F4F0] group-hover:text-[#03070A] transition-colors duration-300" />
                                </span>
                                <span className="font-display text-2xl md:text-4xl tracking-tight text-[#F4F4F0] group-hover:text-[#00E5FF] transition-colors duration-300">
                                    {CONTACT.phoneDisplay}
                                </span>
                                <ArrowUpRight className="w-5 h-5 text-[#8A949E] group-hover:text-[#00E5FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                            </a>
                            <a
                                href={`mailto:${CONTACT.email}`}
                                data-testid="contact-email-link"
                                className="group flex items-center gap-5 w-fit"
                            >
                                <span className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center group-hover:bg-[#00E5FF] group-hover:border-[#00E5FF] transition-colors duration-300">
                                    <Mail className="w-5 h-5 text-[#F4F4F0] group-hover:text-[#03070A] transition-colors duration-300" />
                                </span>
                                <span className="text-lg md:text-2xl text-[#F4F4F0] group-hover:text-[#00E5FF] transition-colors duration-300">
                                    {CONTACT.email}
                                </span>
                                <ArrowUpRight className="w-5 h-5 text-[#8A949E] group-hover:text-[#00E5FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                            </a>
                            <div data-testid="contact-area" className="flex items-center gap-5">
                                <span className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center">
                                    <MapPin className="w-5 h-5 text-[#8A949E]" />
                                </span>
                                <span className="text-base md:text-lg text-[#8A949E]">
                                    Servicing {CONTACT.area}
                                </span>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={0.2}>
                        <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-8 lg:pt-24">
                            <div>
                                <label htmlFor="contact-name" className="text-xs uppercase tracking-[0.25em] text-[#8A949E]">Your name</label>
                                <input
                                    id="contact-name"
                                    data-testid="contact-form-name-input"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Jane Citizen"
                                    className={inputCls}
                                />
                            </div>
                            <div className="grid sm:grid-cols-2 gap-8">
                                <div>
                                    <label htmlFor="contact-email" className="text-xs uppercase tracking-[0.25em] text-[#8A949E]">Email</label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-form-email-input"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="you@email.com"
                                        className={inputCls}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-phone" className="text-xs uppercase tracking-[0.25em] text-[#8A949E]">Phone</label>
                                    <input
                                        id="contact-phone"
                                        data-testid="contact-form-phone-input"
                                        type="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="04xx xxx xxx"
                                        className={inputCls}
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="contact-message" className="text-xs uppercase tracking-[0.25em] text-[#8A949E]">How can we help?</label>
                                <textarea
                                    id="contact-message"
                                    data-testid="contact-form-message-input"
                                    required
                                    rows={4}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Tell us about your pool — size, suburb, and what it needs."
                                    className={`${inputCls} resize-none`}
                                />
                            </div>

                            <button
                                type="submit"
                                data-testid="contact-form-submit-button"
                                disabled={status === "sending"}
                                className="mt-4 self-start rounded-full bg-[#00E5FF] text-[#03070A] px-10 py-4 text-sm font-semibold tracking-wide hover:bg-[#F4F4F0] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 flex items-center gap-2"
                            >
                                {status === "sending" ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    "Send enquiry"
                                )}
                            </button>

                            <AnimatePresence>
                                {status === "success" && (
                                    <motion.p
                                        key="ok"
                                        data-testid="contact-form-success-message"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="text-sm text-[#00E5FF]"
                                    >
                                        Thanks — your enquiry has been sent. We'll be in touch shortly.
                                    </motion.p>
                                )}
                                {status === "error" && (
                                    <motion.p
                                        key="err"
                                        data-testid="contact-form-error-message"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="text-sm text-red-400"
                                    >
                                        Something went wrong. Please call {CONTACT.phoneDisplay} instead.
                                    </motion.p>
                                )}
                            </AnimatePresence>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default Contact;
